import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X, Upload, Image, MagnifyingGlass, Check, FolderOpen, Link, GridFour, ListBullets,
  File, FilePdf, FileDoc, FileXls, MusicNote, VideoCamera
} from '@phosphor-icons/react';

/**
 * MediaPicker - Universal file picker component
 * 
 * Usage:
 * <MediaPicker
 *   isOpen={showPicker}
 *   onClose={() => setShowPicker(false)}
 *   onSelect={(file) => { setImageUrl(file.storage_path); setShowPicker(false); }}
 *   accept="image"  // "image", "document", "audio", "video", "all"
 *   multiple={false}
 * />
 */
export default function MediaPicker({ 
  isOpen, 
  onClose, 
  onSelect, 
  accept = 'all',  // 'image', 'document', 'audio', 'video', 'all'
  multiple = false,
  title = 'Select Media'
}) {
  const [activeTab, setActiveTab] = useState('library'); // 'library', 'upload', 'url'
  const [files, setFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadFiles();
      setSelectedFiles([]);
      setUrlInput('');
    }
  }, [isOpen, accept]);

  const loadFiles = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('folder_id', 'root');
      params.set('limit', '100');
      if (search) params.set('search', search);
      if (accept === 'image') params.set('file_type', 'image');
      else if (accept === 'document') params.set('file_type', 'document');

      const r = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/media/files?${params}`,
        { withCredentials: true }
      );
      setFiles(r.data.files || []);
    } catch (e) {
      toast.error('Failed to load media library');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0) return;

    setUploading(true);
    const uploaded = [];

    for (const file of fileList) {
      try {
        const formData = new FormData();
        formData.append('file', file);

        const r = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/admin/media/upload`,
          formData,
          {
            withCredentials: true,
            headers: { 'Content-Type': 'multipart/form-data' },
          }
        );
        uploaded.push(r.data);
      } catch (e) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    setUploading(false);
    if (uploaded.length > 0) {
      toast.success(`Uploaded ${uploaded.length} file(s)`);
      loadFiles();
      
      // Auto-select uploaded files
      if (multiple) {
        setSelectedFiles(uploaded);
      } else if (uploaded.length === 1) {
        handleConfirm(uploaded);
      }
    }
    e.target.value = '';
  };

  const toggleFileSelection = (file) => {
    if (multiple) {
      setSelectedFiles((prev) =>
        prev.find((f) => f.file_id === file.file_id)
          ? prev.filter((f) => f.file_id !== file.file_id)
          : [...prev, file]
      );
    } else {
      setSelectedFiles([file]);
    }
  };

  const handleConfirm = (filesToConfirm = selectedFiles) => {
    if (filesToConfirm.length === 0 && activeTab === 'library') {
      toast.error('Please select at least one file');
      return;
    }

    if (activeTab === 'url') {
      if (!urlInput.trim()) {
        toast.error('Please enter a URL');
        return;
      }
      onSelect(multiple ? [{ storage_path: urlInput, filename: 'External URL' }] : { storage_path: urlInput, filename: 'External URL' });
      onClose();
      return;
    }

    onSelect(multiple ? filesToConfirm : filesToConfirm[0]);
    onClose();
  };

  const getFileIcon = (file) => {
    if (file.is_image) return <Image size={20} weight="duotone" className="text-blue-600" />;
    if (file.is_audio) return <MusicNote size={20} weight="duotone" className="text-purple-600" />;
    if (file.is_video) return <VideoCamera size={20} weight="duotone" className="text-red-600" />;
    if (file.content_type?.includes('pdf')) return <FilePdf size={20} weight="duotone" className="text-red-600" />;
    if (file.content_type?.includes('word')) return <FileDoc size={20} weight="duotone" className="text-blue-700" />;
    if (file.content_type?.includes('spreadsheet') || file.content_type?.includes('xlsx')) return <FileXls size={20} weight="duotone" className="text-green-600" />;
    return <File size={20} weight="duotone" className="text-muted-foreground" />;
  };

  const getAcceptAttr = () => {
    if (accept === 'image') return 'image/jpeg,image/png,image/webp,image/gif';
    if (accept === 'document') return '.pdf,.docx,.xlsx,.epub';
    if (accept === 'audio') return 'audio/mpeg,audio/wav,audio/ogg';
    if (accept === 'video') return 'video/mp4,video/webm,video/ogg';
    return 'image/*,.pdf,.docx,.xlsx,.epub,audio/*,video/*';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-card border border-border rounded-lg shadow-2xl w-[90vw] max-w-5xl h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h3 className="text-xl font-bold">{title}</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X size={24} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 px-6 pt-4 border-b border-border">
          <button
            onClick={() => setActiveTab('library')}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg ${
              activeTab === 'library'
                ? 'bg-primary/10 text-primary border-b-2 border-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <FolderOpen size={16} className="inline mr-1.5" />
            Media Library
          </button>
          <button
            onClick={() => setActiveTab('upload')}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg ${
              activeTab === 'upload'
                ? 'bg-primary/10 text-primary border-b-2 border-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Upload size={16} className="inline mr-1.5" />
            Upload
          </button>
          <button
            onClick={() => setActiveTab('url')}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg ${
              activeTab === 'url'
                ? 'bg-primary/10 text-primary border-b-2 border-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Link size={16} className="inline mr-1.5" />
            URL
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {/* Library Tab */}
          {activeTab === 'library' && (
            <div className="space-y-4">
              {/* Toolbar */}
              <div className="flex items-center gap-3">
                <div className="relative flex-1">
                  <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && loadFiles()}
                    placeholder="Search files..."
                    className="w-full pl-9 pr-3 py-2 text-sm bg-background border border-border rounded-lg"
                  />
                </div>
                <div className="flex items-center gap-1 border border-border rounded-lg p-1">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-1.5 rounded ${viewMode === 'grid' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`}
                  >
                    <GridFour size={16} />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`}
                  >
                    <ListBullets size={16} />
                  </button>
                </div>
              </div>

              {/* Files Grid/List */}
              {loading && <div className="text-center text-muted-foreground py-12">Loading...</div>}
              {!loading && files.length === 0 && (
                <div className="text-center text-muted-foreground py-12">
                  <FolderOpen size={48} weight="duotone" className="mx-auto mb-3 opacity-50" />
                  <p>No files found</p>
                </div>
              )}

              {!loading && files.length > 0 && (
                <>
                  {viewMode === 'grid' ? (
                    <div className="grid grid-cols-5 gap-3">
                      {files.map((file) => {
                        const isSelected = selectedFiles.find((f) => f.file_id === file.file_id);
                        return (
                          <div
                            key={file.file_id}
                            onClick={() => toggleFileSelection(file)}
                            className={`bg-background border rounded-lg p-2 cursor-pointer relative group hover:border-primary ${
                              isSelected ? 'border-primary ring-2 ring-primary/20' : 'border-border'
                            }`}
                          >
                            {isSelected && (
                              <div className="absolute top-1 right-1 bg-primary text-primary-foreground rounded-full p-0.5">
                                <Check size={12} weight="bold" />
                              </div>
                            )}
                            {file.is_image ? (
                              <img
                                src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${file.thumbnail_path || file.storage_path}`}
                                alt={file.filename}
                                className="w-full h-24 object-cover rounded mb-1"
                              />
                            ) : (
                              <div className="w-full h-24 flex items-center justify-center bg-muted rounded mb-1">
                                {getFileIcon(file)}
                              </div>
                            )}
                            <p className="text-[10px] font-medium truncate" title={file.filename}>
                              {file.filename}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="bg-background border border-border rounded-lg divide-y divide-border">
                      {files.map((file) => {
                        const isSelected = selectedFiles.find((f) => f.file_id === file.file_id);
                        return (
                          <div
                            key={file.file_id}
                            onClick={() => toggleFileSelection(file)}
                            className={`p-3 flex items-center gap-3 cursor-pointer hover:bg-muted ${
                              isSelected ? 'bg-primary/5' : ''
                            }`}
                          >
                            <div className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 ${
                              isSelected ? 'bg-primary border-primary text-primary-foreground' : 'border-border'
                            }`}>
                              {isSelected && <Check size={12} weight="bold" />}
                            </div>
                            {getFileIcon(file)}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{file.filename}</p>
                              <p className="text-xs text-muted-foreground">
                                {(file.size / 1024).toFixed(1)} KB
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Upload Tab */}
          {activeTab === 'upload' && (
            <div className="space-y-4">
              <label className="flex flex-col items-center justify-center border-2 border-dashed border-border rounded-lg p-12 cursor-pointer hover:border-primary hover:bg-primary/5">
                <Upload size={48} weight="duotone" className="text-muted-foreground mb-3" />
                <p className="text-lg font-semibold mb-1">Click to upload or drag and drop</p>
                <p className="text-sm text-muted-foreground">
                  {accept === 'image' && 'JPG, PNG, WebP, GIF up to 10MB'}
                  {accept === 'document' && 'PDF, DOCX, XLSX, EPUB up to 100MB'}
                  {accept === 'audio' && 'MP3, WAV, OGG up to 50MB'}
                  {accept === 'video' && 'MP4, WebM, OGG up to 200MB'}
                  {accept === 'all' && 'Images, documents, audio, video'}
                </p>
                <input
                  type="file"
                  multiple={multiple}
                  accept={getAcceptAttr()}
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </label>
              {uploading && (
                <div className="text-center text-muted-foreground">
                  <div className="animate-spin h-8 w-8 border-b-2 border-primary rounded-full mx-auto mb-2"></div>
                  Uploading...
                </div>
              )}
            </div>
          )}

          {/* URL Tab */}
          {activeTab === 'url' && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-semibold mb-2 block">Enter URL</label>
                <input
                  type="url"
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  placeholder="https://example.com/image.jpg"
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg"
                />
              </div>
              {urlInput && (
                <div className="border border-border rounded-lg p-4">
                  <p className="text-xs text-muted-foreground mb-2">Preview:</p>
                  {accept === 'image' || urlInput.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                    <img src={urlInput} alt="Preview" className="max-w-full max-h-48 rounded" onError={(e) => e.target.style.display = 'none'} />
                  ) : (
                    <p className="text-sm text-muted-foreground">No preview available</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-border">
          <div className="text-sm text-muted-foreground">
            {activeTab === 'library' && selectedFiles.length > 0 && `${selectedFiles.length} selected`}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm border border-border rounded-lg hover:bg-muted"
            >
              Cancel
            </button>
            <button
              onClick={() => handleConfirm()}
              disabled={(activeTab === 'library' && selectedFiles.length === 0) || (activeTab === 'url' && !urlInput.trim())}
              className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg font-semibold disabled:opacity-50"
            >
              {activeTab === 'url' ? 'Use URL' : `Select ${multiple && selectedFiles.length > 1 ? `(${selectedFiles.length})` : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
